import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import AgentHome from "../../pages/AgentHome";
import { useAgentDock } from "../../context/AgentDockContext";
import AgentDockFox from "./AgentDockFox";
import "./AgentDock.css";

const PUSH_MS = 720;
const DIVE_MS = 480;
const EMERGE_MS = 720;

const HIDDEN_PATHS = new Set([
  "/agent",
  "/admin",
  "/knowledge",
  "/A",
  "/B",
  "/C",
  "/D",
  "/hero-compare",
]);

export default function AgentDock() {
  const {
    open,
    phase,
    closeDock,
    advanceToPush,
    advanceToDive,
    advanceToEmerge,
    settleOpen,
    registerOpenSequence,
  } = useAgentDock();
  const [mounted, setMounted] = useState(false);
  const panelRef = useRef(null);
  const timersRef = useRef([]);
  const location = useLocation();
  const path = location.pathname.toLowerCase();
  const hidden = HIDDEN_PATHS.has(path);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn, ms) => {
    const id = window.setTimeout(fn, ms);
    timersRef.current.push(id);
  }, []);

  const startOpenSequence = useCallback(() => {
    if (open || phase !== "idle") return undefined;

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) {
      setMounted(true);
      advanceToPush();
      schedule(advanceToEmerge, 0);
      schedule(settleOpen, 0);
      return undefined;
    }

    setMounted(true);
    advanceToPush();
    schedule(advanceToDive, PUSH_MS);
    schedule(advanceToEmerge, PUSH_MS + DIVE_MS);
    schedule(settleOpen, PUSH_MS + DIVE_MS + EMERGE_MS);
    return undefined;
  }, [
    open,
    phase,
    advanceToPush,
    advanceToDive,
    advanceToEmerge,
    settleOpen,
    schedule,
  ]);

  useEffect(() => {
    registerOpenSequence(startOpenSequence);
    return () => registerOpenSequence(null);
  }, [registerOpenSequence, startOpenSequence]);

  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  useEffect(() => {
    if (hidden && open) closeDock();
  }, [hidden, open, closeDock]);

  useEffect(() => {
    const active = !hidden && open;
    if (!active) {
      document.body.classList.remove("agent-dock-open");
      return undefined;
    }
    document.body.classList.add("agent-dock-open");
    const onKey = (e) => {
      if (e.key === "Escape") closeDock();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("agent-dock-open");
      window.removeEventListener("keydown", onKey);
    };
  }, [open, phase, hidden, closeDock]);

  useEffect(() => clearTimers, [clearTimers]);

  useEffect(() => {
    if (!open) clearTimers();
  }, [open, clearTimers]);

  const updateFoxPerch = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const anchor =
      panel.querySelector(".ah-ask") || panel.querySelector(".ah-composer");
    if (!anchor) return;
    const panelRect = panel.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const perchBottom = Math.round(panelRect.bottom - anchorRect.top);
    panel.style.setProperty("--agent-dock-perch-bottom", `${perchBottom}px`);
  }, []);

  useLayoutEffect(() => {
    if (!open || !mounted) return undefined;
    updateFoxPerch();
    const panel = panelRef.current;
    if (!panel) return undefined;

    window.addEventListener("resize", updateFoxPerch);
    const ro = new ResizeObserver(updateFoxPerch);
    ro.observe(panel);
    const anchor =
      panel.querySelector(".ah-ask") || panel.querySelector(".ah-composer");
    if (anchor) ro.observe(anchor);

    return () => {
      window.removeEventListener("resize", updateFoxPerch);
      ro.disconnect();
    };
  }, [open, mounted, phase, updateFoxPerch]);

  if (hidden) return null;

  const showFab = !open && phase === "idle";
  const showActor = phase === "push" || phase === "dive";

  return (
    <>
      {showFab && (
        <button
          type="button"
          className="agent-dock-fab agent-dock-fab--sleep"
          onClick={startOpenSequence}
          aria-label="Open chat"
          aria-expanded={open}
        >
          <AgentDockFox className="agent-dock-fab__fox" size="md" mood="sleep" />
        </button>
      )}

      {showActor && (
        <div
          className={`agent-dock-actor agent-dock-actor--${phase}`}
          aria-hidden="true"
        >
          <AgentDockFox
            className="agent-dock-actor__fox"
            size="lg"
            mood="sleep"
            bump
            walking={phase === "push" || phase === "dive"}
          />
        </div>
      )}

      {mounted && (
        <aside
          ref={panelRef}
          className={`agent-dock-panel${open ? " agent-dock-panel--open" : ""}${
            phase === "push" || phase === "dive" || phase === "emerge"
              ? " agent-dock-panel--pushing"
              : ""
          } agent-dock-panel--phase-${phase}`}
          role="dialog"
          aria-modal="false"
          aria-label="Chat"
          aria-hidden={!open}
        >
          <div className="agent-dock-panel__clip">
            <AgentHome embedded onClose={closeDock} />
          </div>
        </aside>
      )}
    </>
  );
}
